import React from 'react';
import { Outlet, useLocation, Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import type { RootState } from '../store';
import { useAuth } from '../hooks/useAuth';

const drawerWidth = 232;

const navItems = [
  { label: 'Document Signing', to: '/', icon: <DescriptionOutlinedIcon fontSize="small" /> },
];

export const AppShell: React.FC = () => {
  const { logout } = useAuth();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => (state as any).auth);
  const appsUrl = import.meta.env.VITE_APP_URL_APPS as string | undefined;
  const hasAccess = Array.isArray(user?.roles) && user.roles.includes('access_documents');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" color="inherit" elevation={0} sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1 }}>
              Optima Administration
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              Document Signing
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {appsUrl ? (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<AppsOutlinedIcon fontSize="small" />}
                href={appsUrl}
              >
                Apps
              </Button>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {user?.username || user?.email || 'Signed in'}
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => logout()}
              startIcon={<LogoutIcon fontSize="small" />}
            >
              Logout
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            borderRight: theme => `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', p: 1.5 }}>
          <List sx={{ p: 0 }}>
            {navItems.map(item => (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                selected={item.to === '/' ? location.pathname === '/' || location.pathname === '/documents' : location.pathname === item.to}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Toolbar />
        <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
          {hasAccess ? (
            <Outlet />
          ) : (
            <Alert severity="warning">
              You are signed in, but this account does not have access to document signing administration.
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
};
